import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { formatCurrency } from '../../lib/utils';
import { TrendingUp, Building2 } from 'lucide-react';

const ProjectFormSidebar = ({ formData, calculateAmounts }) => {
  const amounts = calculateAmounts();
  
  return (
    <div className="w-80 flex-shrink-0">
      <div className="sticky top-32 space-y-4">
        {/* Project Details - ย้ายมาด้านบน */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-lg">
              <Building2 className="h-5 w-5" />
              รายละเอียดโปรเจค
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex justify-between">
              <span className="text-sm text-muted-foreground">ชื่อโปรเจค:</span>
              <span className="text-sm font-medium text-right max-w-[200px] truncate">
                {formData.project_name || '-'}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-muted-foreground">ชื่อลูกค้า:</span>
              <span className="text-sm font-medium text-right max-w-[200px] truncate">
                {formData.client_name || '-'}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-muted-foreground">วันที่เริ่ม:</span>
              <span className="text-sm font-medium text-right">
                {formData.start_date ? new Date(formData.start_date).toLocaleDateString('th-TH') : '-'}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-muted-foreground">วันที่จบ:</span>
              <span className="text-sm font-medium text-right">
                {formData.end_date ? new Date(formData.end_date).toLocaleDateString('th-TH') : '-'}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-muted-foreground">จำนวนงวด:</span>
              <span className="text-sm font-medium">
                {formData.payment_schedule?.length || 0} งวด
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-muted-foreground">จำนวน Developer:</span>
              <span className="text-sm font-medium">
                {formData.developer_payments?.length || 0} คน
              </span>
            </div>
          </CardContent>
        </Card>

        {/* Financial Overview */}
        <Card className="border-2 border-primary/20">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-lg">
              <TrendingUp className="h-5 w-5 text-primary" />
              สรุปการเงิน
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {/* มูลค่าก่อน VAT */}
            <div className="flex justify-between items-center py-2">
              <span className="text-sm font-medium text-gray-700">มูลค่าก่อน VAT:</span>
              <span className="text-sm font-bold text-gray-800">
                {formatCurrency(amounts.priceBeforeVat)}
              </span>
            </div>

            {/* มูลค่าจริง (ราคาโปรเจค) */}
            <div className="flex justify-between items-center py-2">
              <span className="text-sm font-medium text-blue-700">
                {formData.price_type === 'include_vat' ? 'มูลค่าจริง (รวม VAT):' : 'มูลค่าจริง (ไม่รวม VAT):'}
              </span>
              <span className="text-sm font-bold text-blue-800">
                {formatCurrency(formData.project_price || 0)}
              </span>
            </div>

            {/* ประเภทราคา */}
            <div className="flex justify-between items-center py-2">
              <span className="text-sm font-medium text-purple-700">ประเภทราคา:</span>
              <span className="text-sm font-bold text-purple-800">
                {formData.price_type === 'include_vat' ? 'รวม VAT' : 'ไม่รวม VAT'}
              </span>
            </div>

            {/* มูลค่ารวม VAT */}
            <div className="flex justify-between items-center py-2">
              <span className="text-sm font-medium text-green-700">มูลค่ารวม VAT:</span>
              <span className="text-sm font-bold text-green-800">
                {formatCurrency(amounts.finalAmount)}
              </span>
            </div>

            {/* ส่วนลด */}
            {(() => {
              try {
                const discount = typeof formData.discount === 'string' ? JSON.parse(formData.discount) : formData.discount;
                if (discount?.enabled && (discount?.percentage > 0 || discount?.amount > 0)) {
                  return (
                    <div className="flex justify-between items-center py-2">
                      <span className="text-sm font-medium text-yellow-700">
                        ส่วนลด {discount.type === 'percentage' ? `${discount.percentage}%` : ''}:
                      </span>
                      <span className="text-sm font-bold text-yellow-800">
                        -{formatCurrency(discount.type === 'percentage' 
                          ? (formData.project_price * discount.percentage / 100)
                          : discount.amount
                        )}
                      </span>
                    </div>
                  );
                }
                return null;
              } catch (e) {
                return null;
              }
            })()}

            {/* ราคาหลังหักส่วนลด */}
            {(() => {
              try {
                const discount = typeof formData.discount === 'string' ? JSON.parse(formData.discount) : formData.discount;
                if (discount?.enabled && (discount?.percentage > 0 || discount?.amount > 0)) {
                  const discountAmount = discount.type === 'percentage' 
                    ? (formData.project_price * discount.percentage / 100)
                    : discount.amount;
                  const priceAfterDiscount = formData.project_price - discountAmount;
                  
                  return (
                    <div className="flex justify-between items-center py-2 border-t border-gray-200">
                      <span className="text-sm font-medium text-green-700">ราคาหลังหักส่วนลด:</span>
                      <span className="text-sm font-bold text-green-800">
                        {formatCurrency(priceAfterDiscount)}
                      </span>
                    </div>
                  );
                }
                return null;
              } catch (e) {
                return null;
              }
            })()}

            {/* VAT */}
            <div className="flex justify-between items-center py-2">
              <span className="text-sm font-medium text-indigo-700">VAT {(() => {
                try {
                  const vat = typeof formData.vat === 'string' ? JSON.parse(formData.vat) : formData.vat;
                  return vat?.percentage || 0;
                } catch (e) {
                  return 0;
                }
              })()}%:</span>
              <span className="text-sm font-bold text-indigo-800">
                {formatCurrency(amounts.vatAmount)}
              </span>
            </div>

            {/* WHT */}
            <div className="flex justify-between items-center py-2">
              <span className="text-sm font-medium text-orange-700">WHT {(() => {
                try {
                  const withholdingTax = typeof formData.withholding_tax === 'string' ? JSON.parse(formData.withholding_tax) : formData.withholding_tax;
                  return withholdingTax?.percentage || 0;
                } catch (e) {
                  return 0;
                }
              })()}%:</span>
              <span className="text-sm font-bold text-orange-800">
                {formatCurrency(amounts.withholdingTaxAmount)}
              </span>
            </div>

            {/* เงินรับจริง */}
            <div className="flex justify-between items-center py-2 border-t border-gray-200">
              <span className="text-sm font-medium text-green-700">เงินรับจริง:</span>
              <span className="text-sm font-bold text-green-800">
                {formatCurrency(amounts.actualReceived)}
              </span>
            </div>

            {/* รายได้บริษัท */}
            <div className="flex justify-between items-center py-2">
              <span className="text-sm font-medium text-purple-700">รายได้บริษัท:</span>
              <span className="text-sm font-bold text-purple-800">
                {formatCurrency(amounts.totalCompanyFeeAmount + amounts.totalSalesCommissionAmount)}
              </span>
            </div>

            {/* ค่าใช้จ่ายโปรเจค */}
            <div className="flex justify-between items-center py-2">
              <span className="text-sm font-medium text-red-700">ค่าใช้จ่ายโปรเจค:</span>
              <span className="text-sm font-bold text-red-800">
                {formatCurrency(amounts.totalOtherExpensesAmount)}
              </span>
            </div>

            {/* ค่าจ้าง Developer */}
            <div className="flex justify-between items-center py-2">
              <span className="text-sm font-medium text-cyan-700">ค่าจ้าง Developer:</span>
              <span className="text-sm font-bold text-cyan-800">
                {formatCurrency(amounts.totalDevPaymentsAmount)}
              </span>
            </div>

            {/* คงเหลือสำหรับ Dev */}
            <div className="flex justify-between items-center py-2 border-t border-gray-200 bg-orange-50 rounded-lg px-3">
              <span className="text-sm font-medium text-orange-700">คงเหลือสำหรับ Dev:</span>
              <span className="text-sm font-bold text-orange-800">
                {formatCurrency(amounts.remainingBeforeDev)}
              </span>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ProjectFormSidebar;
